import  { useState, useEffect, useCallback } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import Swal from "sweetalert2";
import { useToastHandler } from "../../../common/ToastHandler";

const useDebounce = (callback, delay) => {
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  return useCallback((...args) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      callback(...args);
    }, delay);
    setDebounceTimer(timer);
  }, [callback, delay, debounceTimer]);
};



const EmployeeLogController = () => {
  const { Get, getAPI, Delete , _Get, GetPaginatedData } = useApi();
  const [employees, setEmployees] = useState([]);
  const [employeeLogsDateRange, setEmployeeLogsDateRange] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployeeObj, setSelectedEmployeeObj] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [employeeLogs, setEmployeeLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const {handleToast} = useToastHandler()



const clearFilters = () => {
  setSelectedEmployee("");
  setSelectedEmployeeObj(null);
  setStartDate("");
  setEndDate("");
  setEmployeeLogsDateRange([]);
};


  const formatDateForApi = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}${month}${year}`;
  };

  useEffect(() => {
    setLoading(true);
    _Get("user")
      .then((response) => {
        if (response && response.results) {
          setEmployees(response.results);
        }
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);


  const getEmployeeLogData = async (page = 1, search = "") => {
    setIsLoadingData(true);
    try {
      const response = await GetPaginatedData("timeStempData", {
        page: page,
        search: search
      });

      setEmployeeLogs(response.data || response.results || []);
      setTotalPages(Math.ceil((response.totalCount || response.count || 0) / 10) || 1);
      setTotalCount(response.totalCount || response.count || 0);
    } catch (error) {
      console.error("Error fetching employee logs:", error);
      handleToast({
        type: "ERROR",
        message: "Error fetching employee logs.",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    getEmployeeLogData();
  }, []);




  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be delete this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Delete("timeStempData", id)
          .then(() => {
            setEmployeeLogs((prevLogs) =>
              prevLogs.filter((log) => log.id !== id)
            );
            handleToast({
              type:"SUCCESS" ,
              message:"The log has been deleted."
            }) 
          })
          .catch(() => {
            console.error("Error deleting log.");
          });
      }
    });
  };




  const fetchEmployeeLogs = async () => {
    if (selectedEmployee && startDate && endDate) {
      setLoading(true);
      try {
        const formattedStartDate = formatDateForApi(startDate);
        const formattedEndDate = formatDateForApi(endDate);
        const apiUrl = `${getAPI(
          "timeStempData"
        )}?employee_id=${selectedEmployee}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        const response = await Get(apiUrl);
        const logsData = response.results || response;
        
        if (Array.isArray(logsData)) {
          const groupedLogs = {};
          logsData.forEach(log => {
            const date = new Date(log.date_time).toLocaleDateString();
            if (!groupedLogs[date]) {
              groupedLogs[date] = { 
                date, 
                clock_in: null, 
                clock_out: null,
                employee_name: log.employee_name,
                face_image: null
              };
            }
            
            const time = new Date(log.date_time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit"
            });
            
            if (log.is_clocked_in) {
              groupedLogs[date].clock_in = time;
              groupedLogs[date].face_image = log.face_image;
            } else {
              groupedLogs[date].clock_out = time;
              if (!groupedLogs[date].face_image) {
                groupedLogs[date].face_image = log.face_image;
              }
            }
          });
          
          setEmployeeLogsDateRange(Object.values(groupedLogs));
        }
      } catch (error) {
        console.error("Error fetching employee logs:", error);
        setEmployeeLogsDateRange([]);
      } finally {
        setLoading(false);
      }
    }
  };


  const handleEmployeeChange = (opt) => {
    if (opt && opt.id) {
      setSelectedEmployee(opt.id);
      setSelectedEmployeeObj(opt);
    } else {
      setSelectedEmployee("");
      setSelectedEmployeeObj(null);
    }
  };


  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };


  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };


  const handleLogDelete = (logId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be delete this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Delete("timeStempData", logId)
          .then(() => {
            setEmployeeLogs((prevLogs) =>
              prevLogs.filter((log) => log.logId !== logId)
            );
            handleToast({
              type:"SUCCESS" ,
              message:"The log has been deleted."
            }) 
          })
          .catch(() => {
            console.error("Error deleting log.");
          });
      }
    });
  };


  const debouncedGetEmployeeLogData = useDebounce(getEmployeeLogData, 1000);

  
  return {
    employees,
    employeeLogsDateRange,
    selectedEmployee,
    selectedEmployeeObj,
    handleEmployeeChange,
    handleStartDateChange,
    handleEndDateChange,
    startDate,
    endDate,
    fetchEmployeeLogs,
    employeeLogs,
    handleDelete,
    loading,
    handleLogDelete,
    clearFilters,
    getEmployeeLogData,
    debouncedGetEmployeeLogData,
    isLoadingData,
    totalPages,
    totalCount
  };
};
export default EmployeeLogController;